import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, expectJsonBody } from "@/common/lib/api";
import { toast } from "react-toastify";
import { PhoneCountryCodeDigits } from "@/common/utils/phone";
import { useAccountContext } from "@/state/providers/AccountProvider";
import type { DialStep } from "@/features/phone-call-widget/dialSequence";

/**
 * Types for phone call functionality
 */
export interface PhoneNumber {
    id: string;
    type: "caller_id" | "staff_destination";
    staffDestinationType: "phone" | "sip";
    referenceName: string;
    phoneNumber: string;
    /** Present when the practice uses an extension (DTMF) for this number. */
    extension?: string | null;
    /**
     * EN-1050: structured dial sequence backing `extension` — the server's structured source of
     * truth when present, lets a reopened form recover the exact builder mode (simple vs
     * expanded) the vet left it in. `null`/absent for legacy rows written before this feature, in
     * which case callers fall back to parsing `extension`.
     */
    dialSteps?: DialStep[] | null;
    sipUri: string | null;
    verificationStatus: "pending" | "success" | "failed";
    twilioOutgoingCallerIdSid: string | null;
    isArchived: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface AddCallerIdParams {
    phoneNumber: string;
    countryCode: PhoneCountryCodeDigits;
    referenceName: string;
    extension?: string;
    /**
     * EN-1050: structured dial sequence from the phone-tree step builder. Sent alongside
     * `extension` whenever the compiled sequence contains a dialable character, so a server that
     * doesn't recognise `dial_steps` still gets a working `extension` and the feature degrades to
     * today's behaviour.
     *
     * One documented exception: a wait-only sequence (no press step) compiles to an all-comma
     * string, which `hasDialableCharacter` deliberately withholds from `extension` because the
     * server's at-least-one-digit refinement rejects it with a 400 — see `callerIdExtension.ts`.
     * Such a sequence therefore ships as `dial_steps` alone.
     *
     * There is no `.max()` on `extension` in `initiatePhoneVerification`'s body schema (the 32-char
     * cap is `dialExtensionSchema`, the staff-destination sibling), so a compiled caller-ID sequence
     * cannot 400 on length.
     *
     * A pre-EN-1050 server tolerates this payload rather than rejecting it: `InitiateVerificationBodySchema`
     * is a plain `z.object` parsed with `safeParse` (API-Server `app/control/v1/phoneRoutes.ts`) and
     * Zod strips unknown keys, so an old server returns 200 and places the call with no extension —
     * the wait just isn't persisted for the next form open. API-Server still merges first; see §9
     * "Deploy order" in specs/2026-08-25-en1050-caller-id-setup-design.md. When changing either
     * side, run both suites: the shared vector table lives in
     * `src/__tests__/features/phone-call-widget/dialSequence.spec.ts` here and in
     * `app/domain/__tests__/dialSequence.test.ts` there.
     */
    dialSteps?: DialStep[];
}

export interface AddCallerIdResponse extends Omit<PhoneNumber, "type"> {
    type: "caller_id";
    verificationCode?: string;
    verificationCallSid?: string;
}

export interface AddDestinationParams {
    phoneNumber: string;
    countryCode: PhoneCountryCodeDigits;
    referenceName: string;
    extension?: string;
}

export interface UpdatePhoneNumberParams {
    reference_name: string;
    phone_number: string;
    extension?: string | null;
}

export interface InitiateCallParams {
    patientRecordId: string;
    client_phone_number: string;
    staff_phone_number: string;
    account_caller_id: string;
    staff_extension?: string;
}

export interface CallResult {
    callSid: string;
    patientRecordId: string;
    phoneNumber: string;
    status: string;
}

export interface EndCallParams {
    patientRecordId: string;
    callSid: string;
}

/** Realtime payload for call-status-updated (pushed via setQueryData in RealtimeProvider) */
export interface CallStatusPayload {
    callSid: string;
    callStatus: string;
    patientRecordId: string;
    timestamp: string;
}

export interface PhoneCallSettings {
    recording_disclosure_enabled: boolean;
    recording_disclosure_message: string | null;
}

export const PHONE_NUMBER_VERIFICATION_STATUS_QUERY_KEY = ["phone-number-verification-status"] as const;
export const CALL_STATUS_QUERY_KEY = ["call-status"] as const;
export const PHONE_NUMBERS_QUERY_KEY = ["phone-numbers"] as const;
export const PHONE_CALL_SETTINGS_QUERY_KEY = ["phone-call-settings"] as const;

const fetchPhoneCallSettings = async (accountId: string): Promise<PhoneCallSettings> => {
    const response = await apiFetch<PhoneCallSettings>("account/phone-call-settings", {
        method: "GET",
        searchParams: { account_id: accountId },
    });
    return response ?? { recording_disclosure_enabled: false, recording_disclosure_message: null };
};

const updatePhoneCallSettings = async (
    accountId: string,
    settings: Partial<PhoneCallSettings>,
): Promise<PhoneCallSettings> => {
    return expectJsonBody(
        await apiFetch<PhoneCallSettings>("account/phone-call-settings", {
            method: "PATCH",
            searchParams: { account_id: accountId },
            json: settings,
        }),
        "PATCH account/phone-call-settings",
    );
};

const fetchPhoneNumbers = async (): Promise<PhoneNumber[]> => {
    const response = await apiFetch<{ phoneNumbers: PhoneNumber[] }>("phone/numbers", {
        method: "GET",
    });
    return response?.phoneNumbers ?? [];
};

const addCallerId = async (params: AddCallerIdParams): Promise<AddCallerIdResponse> => {
    const phoneNumber = `${params.countryCode}${params.phoneNumber.replace(/\D/g, "")}`;
    const res = await apiFetch<AddCallerIdResponse>("phone/verifications", {
        method: "POST",
        json: {
            phone_number: phoneNumber,
            reference_name: params.referenceName,
            ...(params.extension && { extension: params.extension }),
            // Omitting both keys is how a CLEARED sequence is signalled on retry — absence means
            // "clear", not "leave whatever is stored". The server reads them as `undefined` and
            // writes them onto the reused row unconditionally: `resetPhoneNumberForReverification`
            // (API-Server `app/framework/repo/accountPhoneVerificationRepo.ts`) sets
            // `extension: extension ?? null` and `dial_steps: dialSteps ?? null` in the same UPDATE,
            // so nothing stale can survive. Do not "fix" this by sending an explicit
            // `dial_steps: []` — `buildDialColumns` would then persist an empty JSON array where
            // NULL belongs. Pinned by the clear-on-retry case in
            // `src/__tests__/features/phone-call-widget/CallerIdVerificationModalDialSequence.spec.tsx`.
            ...(params.dialSteps && params.dialSteps.length > 0 && { dial_steps: params.dialSteps }),
        },
    });
    return expectJsonBody(res, "POST phone/verifications");
};

const addDestination = async (params: AddDestinationParams): Promise<PhoneNumber> => {
    const phoneNumber = `${params.countryCode}${params.phoneNumber.replace(/\D/g, "")}`;
    const res = await apiFetch<PhoneNumber>("phone/numbers", {
        method: "POST",
        json: {
            reference_name: params.referenceName,
            phone_number: phoneNumber,
            phone_number_type: "staff_destination",
            staff_destination_type: "phone",
            ...(params.extension && { extension: params.extension }),
        },
    });
    return expectJsonBody(res, "POST phone/numbers");
};

const updatePhoneNumber = async (id: string, params: UpdatePhoneNumberParams): Promise<PhoneNumber> => {
    return expectJsonBody(
        await apiFetch<PhoneNumber>(`phone/numbers/${id}`, { method: "PATCH", json: params }),
        `PATCH phone/numbers/${id}`,
    );
};

const deletePhoneNumber = async (id: string): Promise<void> => {
    await apiFetch(`phone/numbers/${id}`, {
        method: "DELETE",
    });
};

const initiateCall = async (params: InitiateCallParams): Promise<CallResult> => {
    return expectJsonBody(
        await apiFetch<CallResult>(`phone/calls/patient-records/${params.patientRecordId}`, {
            method: "POST",
            json: {
                client_phone_number: params.client_phone_number,
                staff_phone_number: params.staff_phone_number,
                account_caller_id: params.account_caller_id,
                ...(params.staff_extension && { staff_extension: params.staff_extension }),
            },
        }),
        "POST phone/calls/patient-records",
    );
};

const endCall = async (params: EndCallParams): Promise<CallResult> => {
    return expectJsonBody(
        await apiFetch<CallResult>(`phone/calls/${params.callSid}/patient-records/${params.patientRecordId}`, {
            method: "DELETE",
        }),
        "DELETE phone/calls/.../patient-records",
    );
};

export interface PhoneVerification {
    id: string;
    phoneNumber: string;
    referenceName: string;
    verificationStatus: "pending" | "success" | "failed";
    twilioOutgoingCallerIdSid: string | null;
    createdAt: string;
    updatedAt: string;
}

const fetchPhoneVerification = async (
    phoneNumberId: string,
): Promise<{ phoneNumber: string; status: string } | null> => {
    const response = await apiFetch<{ verifications: PhoneVerification[] }>("phone/verifications", {
        method: "GET",
    });
    const match = response?.verifications?.find((v) => v.id === phoneNumberId);
    return match ? { phoneNumber: match.phoneNumber, status: match.verificationStatus } : null;
};

/** Verification statuses that will never change again, so polling can stop. */
const TERMINAL_VERIFICATION_STATUSES = new Set(["success", "failed"]);

export const VERIFICATION_POLL_MS = 4000;

/**
 * Live verification status for a phone_numbers row.
 *
 * Realtime is the fast path — RealtimeProvider pushes into this same query key via setQueryData —
 * but it is NOT the only path. Before EN-1051 this hook's queryFn read its own cache entry and
 * could never reach the server, with staleTime: Infinity, so a single missed broadcast left the
 * modal stuck on "pending" forever even though the database said "success". That violated the
 * standing rule that every realtime-driven flow must keep working when realtime fails.
 *
 * Polling stops as soon as the status is terminal, so the steady-state cost is one request.
 *
 * KNOWN HAZARD (EN-1051, documented not fixed): RealtimeProvider's `setQueryData` and this hook's
 * `queryFn` both write the same query key, and TanStack Query does not order a manual
 * `setQueryData` call against an in-flight fetch. If a poll is already in flight when the realtime
 * broadcast pushes a new status, and that poll's response was captured before the underlying row
 * transitioned, the poll can resolve *after* the broadcast and overwrite the newer value with a
 * stale one — the two writers can race. Not restructured here; see EN-1051 review discussion.
 *
 * What the consumers do about it, precisely — because the previous version of this note was
 * reassuring in a way that hid the actual bug:
 *
 *   - SUCCESS is latched by both consumers (`CallerIdVerificationModal` via `step === "success"`,
 *     `CallerIdVerify` via `isVerified`). That IS protection: a verified number can't flap back to
 *     pending and yank the Continue button away.
 *   - FAILURE is deliberately NOT latched by either. Latching it is not protection, it is a bug:
 *     `CallerIdVerify` used to latch `isFailed` and never reset it, so any terminal "failed" this
 *     hook ever returned — including a stale one for a previous attempt on the same reused row id,
 *     or a late broadcast for an abandoned attempt — pinned the widget to "Not Verified" with no
 *     verification code, permanently, for a call that was ringing at that moment. That was the
 *     headline EN-1051 symptom. Both consumers now derive "failed" from this hook's value so a
 *     later "pending" recovers the UI.
 *
 * So: a new consumer should latch success and derive failure. Do not add a one-way failure guard.
 *
 * @param phoneNumberId - Phone number record id, or null/empty to disable the query
 */
export const usePhoneNumberVerificationStatus = (phoneNumberId: string | null) => {
    const enabled = Boolean(phoneNumberId);
    return useQuery<{ phoneNumber: string; status: string } | null>({
        queryKey: [...PHONE_NUMBER_VERIFICATION_STATUS_QUERY_KEY, phoneNumberId],
        queryFn: () => fetchPhoneVerification(phoneNumberId!),
        enabled,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            // A `null` result (id not yet present in the account's verifications — e.g. the row
            // hasn't propagated yet) deliberately keeps polling rather than stopping: there is no
            // terminal signal to key off, and the modal's own lifetime bounds how long this runs.
            if (status && TERMINAL_VERIFICATION_STATUSES.has(status)) return false;
            return VERIFICATION_POLL_MS;
        },
        staleTime: 0,
        gcTime: 10 * 60 * 1000,
    });
};

/**
 * Subscribes to live call status for a given callSid.
 * Status is pushed via RealtimeProvider (setQueryData on call-status-updated).
 * See: https://tkdodo.eu/blog/using-web-sockets-with-react-query
 *
 * NOTE (EN-1051): this is still the cache-only pattern that was removed from
 * usePhoneNumberVerificationStatus above — realtime is the ONLY path, so a missed broadcast leaves
 * the dialer stuck. Fixing it needs a server endpoint that does not exist yet
 * (GET phone/calls/:sid). Do not copy this shape into new hooks.
 */
export const useCallStatus = (callSid: string | null) => {
    const queryClient = useQueryClient();
    return useQuery<CallStatusPayload | null>({
        queryKey: [...CALL_STATUS_QUERY_KEY, callSid],
        queryFn: () =>
            Promise.resolve(queryClient.getQueryData<CallStatusPayload>([...CALL_STATUS_QUERY_KEY, callSid]) ?? null),
        enabled: !!callSid,
        staleTime: Infinity,
        gcTime: 10 * 60 * 1000,
    });
};

/**
 * React Query hook for phone call recording disclosure settings
 */
export const usePhoneCallSettings = () => {
    const queryClient = useQueryClient();
    const { accountData } = useAccountContext();
    const accountId =
        accountData && typeof accountData === "object" && "id" in accountData ? (accountData.id as string) : undefined;

    const phoneCallSettingsQuery = useQuery({
        queryKey: [...PHONE_CALL_SETTINGS_QUERY_KEY, accountId],
        queryFn: () => fetchPhoneCallSettings(accountId!),
        enabled: Boolean(accountId),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });

    const updatePhoneCallSettingsMutation = useMutation({
        mutationFn: (settings: Partial<PhoneCallSettings>) => updatePhoneCallSettings(accountId!, settings),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PHONE_CALL_SETTINGS_QUERY_KEY });
        },
    });

    return {
        phoneCallSettingsQuery,
        updatePhoneCallSettingsMutation,
    };
};

/**
 * React Query hook for managing phone calls
 */
export const usePhoneCall = () => {
    const queryClient = useQueryClient();

    // Query for fetching all phone numbers (caller IDs and destinations)
    const phoneNumbersQuery = useQuery({
        queryKey: PHONE_NUMBERS_QUERY_KEY,
        queryFn: fetchPhoneNumbers,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });

    // Mutation for adding a caller ID
    const addCallerIdMutation = useMutation({
        mutationFn: addCallerId,
        onSuccess: (data) => {
            // EN-1051: drop any cached verification status for the row this attempt landed on,
            // BEFORE any consumer can read it.
            //
            // The backend resolves a re-verification onto the SAME phone_numbers row, so a second
            // attempt comes back with the same id and the same cache key — and gcTime is 10 minutes.
            // A status consumer mounting on that key reads the previous attempt's terminal "failed",
            // and CallerIdVerificationContent's failed branch renders no code, so it hides the digits
            // for a call Twilio has already placed.
            //
            // This lives here rather than at each retry boundary because the boundaries were never
            // the invariant: what matters is that no consumer mounts on a reused row id and reads a
            // stale terminal status. Every path to a new attempt goes through this mutation
            // (dialer widget, Settings modal, Settings row menu), and `onSuccess` runs before
            // `mutateAsync` resolves — i.e. before the caller can set the state that mounts a
            // consumer. Four earlier scattered `removeQueries` calls were replaced by this one.
            if (data?.id) {
                queryClient.removeQueries({
                    queryKey: [...PHONE_NUMBER_VERIFICATION_STATUS_QUERY_KEY, data.id],
                });
            }
            queryClient.invalidateQueries({ queryKey: PHONE_NUMBERS_QUERY_KEY });
        },
    });

    // Mutation for adding a destination number
    const addDestinationMutation = useMutation({
        mutationFn: addDestination,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PHONE_NUMBERS_QUERY_KEY });
        },
    });

    // Mutation for updating a phone number (e.g. staff_destination)
    const updatePhoneNumberMutation = useMutation({
        mutationFn: ({ id, params }: { id: string; params: UpdatePhoneNumberParams }) => updatePhoneNumber(id, params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PHONE_NUMBERS_QUERY_KEY });
        },
    });

    // Mutation for deleting a phone number
    const deletePhoneNumberMutation = useMutation({
        mutationFn: deletePhoneNumber,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: PHONE_NUMBERS_QUERY_KEY });
        },
    });

    // Mutation for initiating a call
    const initiateCallMutation = useMutation({
        mutationFn: initiateCall,
        onSuccess: () => {
            toast.success("Call initiated");
        },
        onError: () => {
            toast.error("Failed to initiate call");
        },
    });

    // Mutation for ending a call
    const endCallMutation = useMutation({
        mutationFn: endCall,
        onSuccess: () => {
            toast.success("Call ended");
        },
        onError: () => {
            toast.error("Failed to end call");
        },
    });

    // Memoized verified caller IDs - stable reference unless data changes
    const verifiedCallerIds = useMemo(
        () => phoneNumbersQuery.data?.filter((n) => n.type === "caller_id" && n.verificationStatus === "success") ?? [],
        [phoneNumbersQuery.data],
    );

    // All caller IDs regardless of verification state. EN-1051: the settings list previously
    // filtered to "success", so a failed or pending row was invisible AND undeletable — 57 of 151
    // accounts had a failed caller ID and no successful one, and saw an empty list. Kept separate
    // from verifiedCallerIds because the dialer must only ever offer verified numbers.
    const callerIds = useMemo(
        () => phoneNumbersQuery.data?.filter((n) => n.type === "caller_id") ?? [],
        [phoneNumbersQuery.data],
    );

    // Memoized staff destination numbers - stable reference unless data changes
    const staffDestinationNumbers = useMemo(
        () => phoneNumbersQuery.data?.filter((n) => n.type === "staff_destination") ?? [],
        [phoneNumbersQuery.data],
    );

    return {
        // Queries
        phoneNumbersQuery,

        // Mutations
        addCallerIdMutation,
        addDestinationMutation,
        updatePhoneNumberMutation,
        deletePhoneNumberMutation,
        initiateCallMutation,
        endCallMutation,

        // Derived data (memoized)
        verifiedCallerIds,
        callerIds,
        staffDestinationNumbers,
    };
};
